import React, { useCallback, useState } from 'react';
import {
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  List,
  ListItemSecondaryAction,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { Checkbox } from 'src/components/ui/form/Checkbox';

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    listItemDense: {
      padding: 0,
    },
    listItemIcon: {
      minWidth: 0,
      marginRight: theme.spacing(1),
      marginLeft: '2px',
    },
    nestedPrimaryListItemText: {
      fontWeight: 'bold',
    },
    nestedList: {
      paddingLeft: 24,
    },
  })
);

export interface ExpandableListGroupProps {
  id: string;
  label: string;
  expandIcon?: 'plusMinus' | 'arrows';
  withCheckbox?: boolean;
  isSelected?: boolean;
  onCheckboxToggle?: (checked: boolean) => void;
  children: React.ReactNode;
  className?: string;
}

const MemoList = React.memo(List);

export const ExpandableListGroup: React.FC<ExpandableListGroupProps> = ({
  id,
  label,
  withCheckbox = false,
  isSelected = false,
  onCheckboxToggle,
  children,
  className,
}) => {
  const classes = useStyles();
  const [expandedListItems, setExpandedListItems] = useState<string[]>([]);

  const handleExpand = useCallback(() => {
    setExpandedListItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, [id]);

  const handleCheckboxToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      e.stopPropagation();
      onCheckboxToggle?.(checked);
    },
    [onCheckboxToggle]
  );

  const isExpanded = expandedListItems.includes(id);

  return (
    <>
      <ListItem
        button
        dense
        className={`${classes.listItemDense} ${className}`}
        onClick={handleExpand}
        ContainerProps={{
          style: {
            listStyle: 'none',
            padding: 0,
          },
        }}
      >
        <ListItemIcon className={classes.listItemIcon}>
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </ListItemIcon>
        <ListItemText
          classes={{
            primary: classes.nestedPrimaryListItemText,
          }}
        >
          {label}
        </ListItemText>
        {withCheckbox && (
          <ListItemSecondaryAction>
            <Checkbox
              edge="end"
              checked={isSelected}
              onChange={handleCheckboxToggle}
              size="small"
            />
          </ListItemSecondaryAction>
        )}
      </ListItem>
      <Collapse in={isExpanded} mountOnEnter unmountOnExit>
        <MemoList disablePadding dense className={classes.nestedList}>
          {children}
        </MemoList>
      </Collapse>
    </>
  );
};
